import { computed, type Ref, type ComputedRef, type CSSProperties } from 'vue';
import { useVirtualizer } from '@tanstack/vue-virtual';
import {
	VirtualItemType,
	type UserAssignGroup,
	type UserAssignSelectVirtualItem,
} from '../types';

const ITEM_HEIGHT_ESTIMATE = 44;

interface UseUserAssignVirtualListOptions {
	userGroupListToDisplay: ComputedRef<UserAssignGroup[]>;
	closedGroups: Ref<UserAssignGroup['id'][]>;
	scrollElement: Ref<HTMLElement | null>;
}

const createItemStyle = (top: number): CSSProperties => ({
	position: 'absolute',
	top: `${top}px`,
	width: '100%',
});

export function useUserAssignVirtualList({
	userGroupListToDisplay,
	closedGroups,
	scrollElement,
}: UseUserAssignVirtualListOptions) {
	// Flatten groups into sequential array for virtualizer (requires flat list with numeric indices)
	const flattenedItems = computed<UserAssignSelectVirtualItem[]>(() => {
		const items: UserAssignSelectVirtualItem[] = [];

		for (const group of userGroupListToDisplay.value) {
			items.push({ type: VirtualItemType.GroupHeader, group });

			if (closedGroups.value.includes(group.id)) {
				continue;
			}

			if (group.role && group.isRoleSelectable) {
				items.push({
					type: VirtualItemType.Role,
					role: group.role,
					group,
				});
			}

			for (const user of group.userList) {
				items.push({
					type: VirtualItemType.User,
					user,
					group,
				});
			}
		}

		return items;
	});

	const virtualizer = useVirtualizer(
		computed(() => ({
			count: flattenedItems.value.length,
			getScrollElement: () => scrollElement.value,
			estimateSize: () => ITEM_HEIGHT_ESTIMATE,
			overscan: 10,
		}))
	);

	const totalSize = computed(() => virtualizer.value.getTotalSize());

	const virtualListAttrs = computed(() => ({
		style: {
			height: `${totalSize.value}px`,
			position: 'relative' as const,
		},
	}));

	const measureElement = (element: unknown) => {
		if (element instanceof Element || element === null) {
			virtualizer.value.measureElement(element);
		}
	};

	const scrollToIndex = (index: number) => {
		virtualizer.value.scrollToIndex(index, {
			align: 'start',
		});
	};

	/**
	 * Re-group visible items back into hierarchical structure for template.
	 *
	 * Why not just use flat structure? Each group is a separate component
	 * (UserAssignSelectListGroup) that renders semantic HTML (role="group", aria-label).
	 */
	const groupedVirtualList = computed<UserAssignGroup[]>(() => {
		const virtualItems = virtualizer.value.getVirtualItems();
		const groups = new Map<string, UserAssignGroup>();

		for (const vItem of virtualItems) {
			const data = flattenedItems.value[vItem.index];
			const sourceGroup = data.group;
			let group = groups.get(sourceGroup.id);

			if (!group) {
				group = {
					...sourceGroup,
					children: [],
					hasVisibleHeader: false,
					headerStyle: createItemStyle(0),
				};
				groups.set(sourceGroup.id, group);
			}

			if (data.type === VirtualItemType.GroupHeader) {
				group.hasVisibleHeader = true;
				group.headerIndex = vItem.index;
				group.headerStyle = createItemStyle(vItem.start);
			} else {
				group.children?.push({
					data,
					index: vItem.index,
					start: vItem.start,
					size: vItem.size,
					style: createItemStyle(vItem.start),
				});
			}
		}

		return [...groups.values()];
	});

	return {
		groupedVirtualList,
		totalSize,
		virtualListAttrs,
		measureElement,
		scrollToIndex,
	};
}
